import { isNavigationAbortMessage } from './navigation-abort';

/**
 * A full document navigation cancels every request the old page still has in
 * flight. React Router turns a cancelled `.data` request into a route error, so
 * the old page swaps to an error page for the moment it has left before the new
 * document commits. Firefox makes this visible: its 500ms post-login wait (see
 * `handleAuthRedirect`) sits exactly where the home page's post-login fetchers
 * are still loading.
 *
 * Code that replaces the document marks the navigation as pending first. While
 * it is pending, `withDocumentNavigationAbortHold` holds back the rejection of a
 * cancelled `.data` request, so React Router never sees it and the old page stays
 * on screen until the new one replaces it.
 *
 * Covered: navigations started through `assignDocumentLocation` /
 * `reloadDocument`, and clicks on plain links that load a new document (see
 * `holdAbortsOnDocumentLinkClicks`) — e.g. a Business Manager nav link from the
 * Remix dashboard to a legacy BM page. A user typing a URL or pressing reload is
 * not, because detecting that needs a `beforeunload` listener, and Firefox
 * refuses the back/forward cache to any page that registers one.
 */

/**
 * How long a marked navigation may take to commit before cancelled requests are
 * released again. Long enough for a slow connection to fetch the next document;
 * short enough that a navigation which never happens still ends on the
 * self-healing `<NetworkError />` page rather than a page that silently stopped
 * loading.
 */
export const DOCUMENT_NAVIGATION_GRACE_MS = 10_000;

let pendingSince: number | null = null;
let clearsOnRestore = false;

export const clearDocumentNavigationPending = (): void => {
    pendingSince = null;
};

/** A page restored from the back/forward cache is live again, not leaving. */
const clearOnRestore = (event: PageTransitionEvent) => {
    if (event.persisted) clearDocumentNavigationPending();
};

export const markDocumentNavigationPending = (
    now: number = Date.now(),
): void => {
    pendingSince = now;
    if (clearsOnRestore || typeof window === 'undefined') return;
    // `pageshow`, unlike `beforeunload`, leaves the page eligible for bfcache.
    window.addEventListener('pageshow', clearOnRestore);
    clearsOnRestore = true;
};

/** Milliseconds left in the grace window; 0 when no navigation is pending. */
export const documentNavigationGraceRemaining = (
    now: number = Date.now(),
): number => {
    if (pendingSince === null) return 0;
    return Math.max(0, pendingSince + DOCUMENT_NAVIGATION_GRACE_MS - now);
};

/** `window.location.href = url`, marked so cancelled requests don't flash an error page. */
export const assignDocumentLocation = (url: string): void => {
    markDocumentNavigationPending();
    window.location.href = url;
};

/** `window.location.reload()`, marked so cancelled requests don't flash an error page. */
export const reloadDocument = (): void => {
    markDocumentNavigationPending();
    window.location.reload();
};

const withoutHash = (url: URL): string =>
    `${url.origin}${url.pathname}${url.search}`;

/** True when following `href` replaces this document rather than jumping within it. */
const loadsNewDocument = (href: string): boolean => {
    try {
        const url = new URL(href, window.location.href);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
        // `url.hash` is '' for a bare `#`, so test the serialised href instead.
        const isFragmentJump =
            url.href.includes('#') &&
            withoutHash(url) === withoutHash(new URL(window.location.href));
        return !isFragmentJump;
    } catch {
        return false;
    }
};

/**
 * Link targets that replace this document: this tab, or — from inside a frame —
 * the frame itself goes away. Browsers match these keywords case-insensitively.
 */
const DOCUMENT_REPLACING_TARGETS = new Set(['', '_self', '_parent', '_top']);

/**
 * A click the browser is about to turn into a document load: primary button, no
 * modifier keys, on an http(s) link without `download` or a new-tab `target`,
 * and not already handled with `preventDefault` — which is how React Router's
 * `<Link>` claims a client-side navigation.
 */
export const isDocumentLinkClick = (event: MouseEvent): boolean => {
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return false;
    }
    const anchor =
        event.target instanceof Element
            ? event.target.closest('a[href]')
            : null;
    if (!(anchor instanceof HTMLAnchorElement)) return false;
    if (anchor.hasAttribute('download')) return false;
    if (!DOCUMENT_REPLACING_TARGETS.has(anchor.target.toLowerCase())) {
        return false;
    }
    return loadsNewDocument(anchor.href);
};

/** The mark a link click made, so later input clears only that one. */
let linkClickMarkedAt: number | null = null;

const markOnDocumentLinkClick = (event: MouseEvent) => {
    if (!isDocumentLinkClick(event)) return;
    markDocumentNavigationPending();
    linkClickMarkedAt = pendingSince;
};

/**
 * A click only predicts a navigation: Escape, a 204 or a download leaves the
 * page where it is. More input on the old page shows it is still live, so drop
 * the click's mark and let a genuine network failure through straight away
 * instead of up to `DOCUMENT_NAVIGATION_GRACE_MS` later. Marks set by
 * `assignDocumentLocation` / `reloadDocument` are left alone.
 */
const clearLinkClickMarkOnInput = () => {
    if (linkClickMarkedAt !== null && pendingSince === linkClickMarkedAt) {
        clearDocumentNavigationPending();
    }
    linkClickMarkedAt = null;
};

const INPUT_EVENTS = ['pointerdown', 'keydown'] as const;

/**
 * Mark a document navigation as pending whenever the user clicks a plain link
 * that loads a new document, so the old page's cancelled `.data` requests don't
 * flash an error page while the next document loads. Returns a disposer.
 *
 * Call it right after `hydrateRoot(document, …)`. The listener then sits on
 * `document` after React's root listener, so it runs once a `<Link>` has called
 * `preventDefault` for a client-side navigation (correctly ignored), and a
 * synthetic `stopPropagation()` — which stops the event at `document` — does not
 * hide the click from it. `pointerdown` and `keydown` come before the `click`
 * they start, so they only ever clear an earlier click's mark.
 */
export const holdAbortsOnDocumentLinkClicks = (): (() => void) => {
    document.addEventListener('click', markOnDocumentLinkClick);
    INPUT_EVENTS.forEach((type) =>
        document.addEventListener(type, clearLinkClickMarkOnInput, true),
    );
    return () => {
        document.removeEventListener('click', markOnDocumentLinkClick);
        INPUT_EVENTS.forEach((type) =>
            document.removeEventListener(type, clearLinkClickMarkOnInput, true),
        );
        linkClickMarkedAt = null;
    };
};

const requestUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
};

/** A same-origin React Router single-fetch request (`/_root.data`, `/remix-api/x.data`). */
export const isSameOriginDataRequest = (input: RequestInfo | URL): boolean => {
    try {
        const url = new URL(requestUrl(input), window.location.href);
        return (
            url.origin === window.location.origin &&
            url.pathname.endsWith('.data')
        );
    } catch {
        return false;
    }
};

/**
 * Wrap `fetch` so a `.data` request cancelled by a marked document navigation
 * rejects only after the grace window, if at all. React Router calls the global
 * `fetch` for every loader and fetcher, so wrapping it once in `entry.client.tsx`
 * covers them all. Everything else — other URLs, other failures, and any
 * failure with no navigation pending — passes through untouched.
 */
export const withDocumentNavigationAbortHold =
    (fetchImpl: typeof fetch): typeof fetch =>
    (input, init) =>
        fetchImpl(input, init).catch((error: unknown) => {
            const wait = documentNavigationGraceRemaining();
            const isCancelledDataRequest =
                error instanceof Error &&
                isNavigationAbortMessage(error.message) &&
                isSameOriginDataRequest(input);
            if (wait === 0 || !isCancelledDataRequest) throw error;
            return new Promise<Response>((_resolve, reject) => {
                setTimeout(() => reject(error), wait);
            });
        });
